import type { ExamCategory, ExamCategoryTreeNode } from "./types"

export function getRootCategories(categories: ExamCategory[]): ExamCategory[] {
  return categories
    .filter((category) => category.parentId === null && category.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
}

export function getChildCategories(
  categories: ExamCategory[],
  parentId: string
): ExamCategory[] {
  return categories
    .filter((category) => category.parentId === parentId && category.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
}

export function getLeafCategories(categories: ExamCategory[]): ExamCategory[] {
  const active = categories.filter((category) => category.isActive)
  return active.filter(
    (category) =>
      !active.some((candidate) => candidate.parentId === category.id)
  )
}

export function findCategory(
  categories: ExamCategory[],
  categoryId: string
): ExamCategory | undefined {
  return categories.find((category) => category.id === categoryId)
}

export function getRootCategoryId(
  categories: ExamCategory[],
  categoryId: string
): string | null {
  let current = findCategory(categories, categoryId)
  if (!current) return null
  while (current.parentId) {
    const parent = findCategory(categories, current.parentId)
    if (!parent) break
    current = parent
  }
  return current.id
}

export function getDescendantCategoryIds(
  categories: ExamCategory[],
  rootId: string
): string[] {
  const ids: string[] = []
  const visit = (parentId: string) => {
    for (const child of getChildCategories(categories, parentId)) {
      ids.push(child.id)
      visit(child.id)
    }
  }
  visit(rootId)
  return ids
}

export function buildCategoryTree(
  categories: ExamCategory[]
): ExamCategoryTreeNode[] {
  const attach = (node: ExamCategory): ExamCategoryTreeNode => ({
    ...node,
    children: getChildCategories(categories, node.id).map(attach),
  })
  return getRootCategories(categories).map(attach)
}

export function getCategoryPathLabel(
  categories: ExamCategory[],
  categoryId: string
): string {
  const labels: string[] = []
  let current = findCategory(categories, categoryId)
  while (current) {
    labels.unshift(current.name)
    current = current.parentId
      ? findCategory(categories, current.parentId)
      : undefined
  }
  return labels.join(" · ")
}

export function getCategoryDepth(
  categories: ExamCategory[],
  categoryId: string
): number {
  let depth = 0
  let current = findCategory(categories, categoryId)
  while (current?.parentId) {
    depth += 1
    current = findCategory(categories, current.parentId)
  }
  return depth
}

export function getCategoryLevelLabel(depth: number): string {
  switch (depth) {
    case 0:
      return "Domain"
    case 1:
      return "Skill"
    case 2:
      return "Target"
    default:
      return "Detail"
  }
}

export function getChildLevelLabel(depth: number): string {
  return getCategoryLevelLabel(depth + 1)
}

export function getChildCountLabel(depth: number, count: number): string {
  if (count === 0) return ""
  const childLevel = getChildLevelLabel(depth).toLowerCase()
  const plural = count === 1 ? childLevel : `${childLevel}s`
  return `${count} ${plural}`
}

export function getCategoryAncestors(
  categories: ExamCategory[],
  categoryId: string
): string[] {
  const ids: string[] = []
  let current = findCategory(categories, categoryId)
  while (current?.parentId) {
    ids.unshift(current.parentId)
    current = findCategory(categories, current.parentId)
  }
  return ids
}

export function getCategoryBreadcrumb(
  categories: ExamCategory[],
  categoryId: string
): ExamCategory[] {
  const trail: ExamCategory[] = []
  let current = findCategory(categories, categoryId)
  while (current) {
    trail.unshift(current)
    current = current.parentId
      ? findCategory(categories, current.parentId)
      : undefined
  }
  return trail
}

export function countCategoriesByLevel(categories: ExamCategory[]): {
  domains: number
  skills: number
  targets: number
  details: number
  total: number
} {
  const active = categories.filter((category) => category.isActive)
  const counts = {
    domains: 0,
    skills: 0,
    targets: 0,
    details: 0,
    total: active.length,
  }
  for (const category of active) {
    const depth = getCategoryDepth(active, category.id)
    if (depth === 0) counts.domains += 1
    else if (depth === 1) counts.skills += 1
    else if (depth === 2) counts.targets += 1
    else counts.details += 1
  }
  return counts
}

export function isCategoryVisibleInSearch(
  categories: ExamCategory[],
  categoryId: string,
  query: string
): boolean {
  const trimmed = query.trim()
  if (!trimmed) return true
  const needle = trimmed.toLowerCase()
  const node = findCategory(categories, categoryId)
  if (!node) return false
  if (
    node.name.toLowerCase().includes(needle) ||
    node.description?.toLowerCase().includes(needle)
  ) {
    return true
  }
  return getDescendantCategoryIds(categories, categoryId).some((id) => {
    const descendant = findCategory(categories, id)
    return (
      descendant?.name.toLowerCase().includes(needle) ||
      descendant?.description?.toLowerCase().includes(needle)
    )
  })
}

export function getSearchExpandedIds(
  categories: ExamCategory[],
  query: string
): Set<string> {
  const trimmed = query.trim()
  const expanded = new Set<string>()
  if (!trimmed) return expanded
  const needle = trimmed.toLowerCase()
  for (const category of categories) {
    if (!category.isActive) continue
    if (
      !category.name.toLowerCase().includes(needle) &&
      !category.description?.toLowerCase().includes(needle)
    ) {
      continue
    }
    for (const ancestorId of getCategoryAncestors(categories, category.id)) {
      expanded.add(ancestorId)
    }
  }
  return expanded
}
